import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, AuthContextType, RegisterData } from '../types';
import { authAPI } from '../services/api';
import { decryptData, encryptData } from '../utils/encryption';
import { useAutoLogout } from '../hooks/useAutoLogout';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(decryptData(storedUser)) || null);
          
          // Verify token is still valid
          const response = await authAPI.verify();
          if (response.success && response.data) {            
            setUser(response.data.user);
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string, sessionToken?: string) => {
    try {
      setLoading(true);
      const payload = {
        email: email,
        password: password,
      }
      const response = await authAPI.login(encryptData(payload));      
      if (response.success && response.data) {
        const { user: userData, token: userToken } = response.data;
        
        setUser(userData);
        setToken(userToken);

        localStorage.setItem('token', userToken);
        localStorage.setItem('user', encryptData(JSON.stringify(userData)));
        localStorage.setItem('permissions', encryptData(userData.invoice_permission));

        // If session token is provided (MFA flow), store it
        if (sessionToken) {
          localStorage.setItem('mfa_session_token', sessionToken);
        }
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.error || error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);
      
      if (response.success && response.data) {
        const { user: newUser, token: userToken } = response.data;
        
        setUser(newUser);
        setToken(userToken);
        
        localStorage.setItem('token', userToken);
        localStorage.setItem('user', encryptData(JSON.stringify(newUser)));
        localStorage.setItem('permissions', encryptData(newUser.invoice_permission));
      } else {
        throw new Error(response.error || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(error.response?.data?.error || error.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await authAPI.logout();
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
  };

  // Auto-logout on inactivity
  useAutoLogout({
    isAuthenticated: !!user && !!token,
    logout,
  });

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    setAuthenticated: (newUser, newToken) => {
      setUser(newUser);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', encryptData(JSON.stringify(newUser)));
      localStorage.setItem('permissions', encryptData(newUser.invoice_permission));
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 